import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { PUBLIC_API_URL } from '@src/config'
import axios from 'axios'
import { parseBidData } from '@src/utility/bidData'

// Async Thunk
export const fetchBidByNumber = createAsyncThunk(
    'bids/fetchBidByNumber',
    async (bidNumber, thunkAPI) => {
        try {
            const url = `${PUBLIC_API_URL}/api/bids/${bidNumber}`
            const { data } = await axios.get(url)
            return data
        } catch (error) {
            return thunkAPI.rejectWithValue({ error: error.message })
        }
    }
)

const bidsSlice = createSlice({
    name: 'bids',
    initialState: {
        searchQuery: '',
        table: [],
        bidNo: 0,
        consignment: false,
        baseCurrency: "USD",
        selectedRows: new Set(),
        bidNumber: 0,
        poNo: 0,
        loading: true,
        itemsLoaded: 0,
        sidebarValues: {},
        calculateValues: {
            type: "franchise",
            percentage: 10
        },
        bidAuth: '',
        error: null,
        unsavedChanges: false,
        roundedToTwoDP: false
    },
    reducers: {
        setBaseCurrency: (state, action) => {
            state.baseCurrency = action.payload
            state.unsavedChanges = true
        },
        setSelectedRows: (state, action) => {
            state.selectedRows = action.payload
        },
        setTable: (state, action) => {
            state.table = action.payload
            state.unsavedChanges = true
        },
        setSidebarValues: (state, action) => {
            state.sidebarValues = action.payload
            if (!action.payload.contact?.initial || action.payload.contact?.initial === 'undefined') {
                state.unsavedChanges = true
            }
        },
        setItemsLoaded: (state, action) => {
            state.itemsLoaded = action.payload
        },
        setCalculateValues: (state, action) => {
            state.calculateValues = action.payload
            state.unsavedChanges = true
        },
        setBidNumber: (state, action) => {
            state.bidNumber = action.payload
        },
        setPoNo: (state, action) => {
            state.poNo = action.payload
        },
        setConsignment: (state, action) => {
            state.consignment = action.payload
        },
        setUnsavedChanges: (state, action) => {
            state.unsavedChanges = action.payload
        },
        setBidAuth: (state, action) => {
            state.bidAuth = action.payload
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload
        },
        setRoundedToTwoDP: (state, action) => {
            state.roundedToTwoDP = action.payload
        },
        // Reset state values
        resetValues: (state) => {
            state.table = []
            state.baseCurrency = "USD"
            state.selectedRows = new Set()
            state.bidNumber = 0
            state.poNo = 0
            state.loading = false
            state.itemsLoaded = 0
            state.sidebarValues = {}
            state.calculateValues = {
                type: "franchise",
                percentage: 10
            }
            state.consignment = false
            state.error = null
            state.unsavedChanges = false
            state.bidAuth = ''
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBidByNumber.pending, (state) => {
                state.loading = true
            })
            .addCase(fetchBidByNumber.fulfilled, (state, action) => {
                // Note: the backend now returns bidData in camelCase.
                // parseBidData returns null when the stored JSON cannot be
                // recovered - surface that instead of silently showing an empty
                // bid the user could then save over the real lines.
                const parsedData = parseBidData(action.payload.data.bidData)

                state.loading = false
                state.error = parsedData === null ? 'Unable to read the bid lines for this bid.' : null
                state.table = Array.isArray(parsedData)
                    ? parsedData.map((item, index) => {
                        // Assuming the JSON now uses camelCase property names
                        const {
                            bidPrice,
                            supplierPrice,
                            customPrice,
                            franchisedPrice,
                            advertisedPrice,
                            mfr,
                            ...rest
                        } = item

                        return {
                            ...rest,
                            bidPrice,
                            supplierPrice,
                            customPrice,
                            franchisedPrice,
                            advertisedPrice,
                            mfr,
                            itemNo: index
                        }
                    })
                    : []

                // Use camelCase fields from the backend response
                state.poNo = action.payload.data.poNo
                state.bidNumber = action.payload.data.bidNo
                state.consignment = action.payload.data.consignment === 'T'
                state.sidebarValues = {
                    name: action.payload.data.rep,
                    currency: {
                        label: action.payload.data.currency,
                        value: action.payload.data.currency,
                        id: action.payload.data.currencyId,
                        symbol: action.payload.data.symbol
                    },
                    terms: {
                        label: action.payload.data.terms,
                        value: action.payload.data.termsId
                    },
                    contact: {
                        label: action.payload.data.contact,
                        value: action.payload.data.contactId
                    },
                    salesReps: action.payload.data.salesReps,
                    source: action.payload.data.source,
                    comments: action.payload.data.comments,
                    company: { value: action.payload.data.companyNo, label: action.payload.data.company }
                }
                state.calculateValues = {
                    type: action.payload.data.bidType,
                    percentage: action.payload.data.bidPercentage
                }
                state.bidAuth = action.payload.data.bidAuth
            })
            .addCase(fetchBidByNumber.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload.error
            })
    }
})

export const {
    setBaseCurrency,
    setSelectedRows,
    setTable,
    setSidebarValues,
    setItemsLoaded,
    setCalculateValues,
    setBidNumber,
    setRoundedToTwoDP,
    setConsignment,
    setPoNo,
    resetValues,
    setUnsavedChanges,
    setBidAuth,
    setSearchQuery
} = bidsSlice.actions

export default bidsSlice.reducer
